<template>
	<div class="product-page pb-12">
		<v-container v-if="producto">
			<vue-headful
				:title="producto.name"
				:description="producto.description"
				:keywords="producto.name"
				:image="producto.image_url"
				lang="es"
				ogLocale="es"
				:url="`${api.url.replace('public', 'store')}#/product/${encodeURIComponent(producto.referencia)}`"
			/>

			<!-- Breadcrumb Navigation -->
			<div class="d-flex align-center py-3 mb-2 text-caption text-secondary-custom">
				<router-link to="/" class="text-secondary-custom text-decoration-none hover-underline">
					{{ "store" | trans }}
				</router-link>
				<v-icon x-small class="mx-2">mdi-chevron-right</v-icon>
				<template v-if="producto.categoria">
					<router-link
						:to="`/category/${producto.categoria.id}`"
						class="text-secondary-custom text-decoration-none hover-underline"
					>
						{{ producto.categoria.nombre }}
					</router-link>
					<v-icon x-small class="mx-2">mdi-chevron-right</v-icon>
				</template>
				<span class="text-slate-800 font-weight-medium text-truncate" style="max-width: 300px">
					{{ producto.name }}
				</span>
			</div>

			<v-row>
				<!-- Column 1: Gallery & Zoom -->
				<v-col cols="12" md="5" lg="4" class="order-1">
					<v-card class="gallery-card pa-4 rounded-xl" flat outlined>
						<product-zoom :src="producto.image_url" :alt="producto.name" height="340px"></product-zoom>

						<!-- Thumbnail Carousel -->
						<v-slide-group
							v-if="producto.images && producto.images.length > 1"
							v-model="selectedImageIndex"
							show-arrows
							center-active
							mandatory
							class="mt-4"
						>
							<template v-slot:prev>
								<v-btn icon x-small @click.stop="prevImage">
									<v-icon>mdi-chevron-left</v-icon>
								</v-btn>
							</template>
							<template v-slot:next>
								<v-btn icon x-small @click.stop="nextImage">
									<v-icon>mdi-chevron-right</v-icon>
								</v-btn>
							</template>
							<v-slide-item
								v-for="(img, idx) in producto.images"
								:key="img.id || idx"
								v-slot:default="{ active, toggle }"
								class="px-1"
							>
								<div
									class="thumbnail-item"
									:class="{ active: active }"
									@click="
										selectImage(idx);
										toggle();
									"
								>
									<v-img :src="img.url" height="60px" width="60px" contain class="rounded"></v-img>
								</div>
							</v-slide-item>
						</v-slide-group>
					</v-card>
				</v-col>

				<!-- Column 2: Product Info -->
				<v-col cols="12" md="7" lg="5" class="order-3 order-md-2 px-md-6">
					<!-- Category Badges -->
					<div class="mb-2" v-if="producto.categorias && producto.categorias.length">
						<span
							v-for="cat in producto.categorias"
							:key="cat.id"
							class="category-pill-lg mr-2 mb-1"
							@click="$router.push(`/category/${cat.id}`)"
						>
							{{ cat.nombre }}
						</span>
					</div>

					<!-- Product Title -->
					<h1 class="product-detail-title text-slate-900 font-weight-bold mb-3">
						{{ producto.name }}
					</h1>

					<!-- SKU / Reference & Condition Badges -->
					<div class="d-flex align-center flex-wrap mb-4 meta-badges">
						<div class="meta-tag mr-3 mb-2">
							<span class="text-secondary-custom">{{ "store.reference" | trans }}:</span>
							<strong class="ml-1 text-slate-800">{{ producto.referencia }}</strong>
						</div>
						<div class="meta-tag mr-3 mb-2">
							<span class="text-secondary-custom">{{ "store.condition" | trans }}:</span>
							<span class="ml-1 text-success font-weight-bold">{{ "store.new" | trans }}</span>
						</div>
						<div class="meta-tag mb-2" v-if="producto.mostrar_stock">
							<span class="stock-pill in-stock font-weight-bold">
								{{ "store.stock" | trans }}: {{ producto.stock | number }}
							</span>
						</div>
						<div class="meta-tag mb-2" v-else-if="producto.stock > 0">
							<span class="stock-pill in-stock font-weight-bold">
								{{ "store.stock_available" | trans }}
							</span>
						</div>
					</div>

					<v-divider class="my-4"></v-divider>

					<!-- Description & Details -->
					<div class="product-description-container mb-6">
						<h4 class="text-subtitle-2 font-weight-bold text-slate-800 mb-2">Descripción del Producto</h4>
						<div
							class="product-description-text text-slate-700"
							v-if="producto.description"
							v-html="producto.description"
						></div>
						<div class="text-caption text-secondary-custom font-italic" v-else>
							{{ "store.no_description" | trans }}
						</div>

						<div class="mt-4" v-if="producto.notas">
							<h4 class="text-subtitle-2 font-weight-bold text-slate-800 mb-2">Notas adicionales</h4>
							<div class="product-description-text text-slate-700" v-html="producto.notas"></div>
						</div>
					</div>

					<!-- Social Share -->
					<div class="share-section pt-2">
						<span class="caption font-weight-bold text-secondary-custom d-block mb-2">{{
							"store.share_on" | trans
						}}</span>
						<div class="d-flex align-center flex-wrap">
							<ShareNetwork
								network="whatsapp"
								:url="`${api.url.replace('public', 'store')}#/product/${encodeURIComponent(producto.referencia)}`"
								:title="producto.name"
								:description="producto.description"
								class="mr-2 mb-2"
							>
								<v-btn icon small color="#25D366" class="share-btn"><v-icon small>mdi-whatsapp</v-icon></v-btn>
							</ShareNetwork>
							<ShareNetwork
								network="facebook"
								:url="`${api.url.replace('public', 'store')}#/product/${encodeURIComponent(producto.referencia)}`"
								:title="producto.name"
								:description="producto.description"
								class="mr-2 mb-2"
							>
								<v-btn icon small color="#1877F2" class="share-btn"><v-icon small>mdi-facebook</v-icon></v-btn>
							</ShareNetwork>
							<ShareNetwork
								network="twitter"
								:url="`${api.url.replace('public', 'store')}#/product/${encodeURIComponent(producto.referencia)}`"
								:title="producto.name"
								:description="producto.description"
								class="mr-2 mb-2"
							>
								<v-btn icon small color="#1DA1F2" class="share-btn"><v-icon small>mdi-twitter</v-icon></v-btn>
							</ShareNetwork>
							<ShareNetwork
								network="email"
								:url="`${api.url.replace('public', 'store')}#/product/${encodeURIComponent(producto.referencia)}`"
								:title="producto.name"
								:description="producto.description"
								class="mr-2 mb-2"
							>
								<v-btn icon small color="#EA4335" class="share-btn"><v-icon small>mdi-email-outline</v-icon></v-btn>
							</ShareNetwork>
						</div>
					</div>
				</v-col>

				<!-- Column 3: Purchase Card -->
				<v-col cols="12" md="12" lg="3" class="order-2 order-md-3">
					<v-card class="purchase-card pa-6 rounded-xl" flat outlined>
						<!-- Price -->
						<div class="mb-4" v-if="producto.mostrar_precio">
							<span class="caption text-secondary-custom d-block mb-1">{{ "price" | trans }}</span>
							<div class="main-price-display font-weight-bold text-slate-900">
								{{ producto | price | money }}
							</div>
						</div>

						<v-divider class="my-4" v-if="producto.mostrar_precio"></v-divider>

						<!-- Quantity Stepper -->
						<div class="mb-4">
							<label class="caption font-weight-bold text-slate-800 d-block mb-2">{{ "quantity" | trans }}</label>
							<div class="quantity-stepper d-flex align-center">
								<v-btn icon small @click="addProduct(-1)" class="stepper-btn" :disabled="quantity <= 1">
									<v-icon small>mdi-minus</v-icon>
								</v-btn>
								<v-text-field
									v-model.number="quantity"
									type="number"
									min="1"
									dense
									flat
									hide-details
									solo
									class="stepper-input text-center"
								></v-text-field>
								<v-btn icon small @click="addProduct(1)" class="stepper-btn">
									<v-icon small>mdi-plus</v-icon>
								</v-btn>
							</div>
						</div>

						<!-- Advanced Stock Selector -->
						<div class="mb-4" v-if="producto.advanced_stock && producto.advanced_stock.length > 0">
							<stock-selector :product="producto" v-model="stockSelected"></stock-selector>
						</div>

						<!-- Action Buttons -->
						<v-btn
							block
							large
							depressed
							dark
							color="#0f172a"
							class="rounded-pill mb-2 add-to-cart-cta text-capitalize"
							:disabled="
								producto.advanced_stock && producto.advanced_stock.length > 0 && (!stockSelected || quantity <= 0)
							"
							@click="addToCart"
						>
							<v-icon left small color="#38bdf8">mdi-cart-plus</v-icon>
							<span class="text-white font-weight-bold">{{ "store.add_to_cart" | trans }}</span>
						</v-btn>

						<v-btn block large outlined color="success" class="rounded-pill mb-4 text-capitalize" to="/cart">
							<v-icon left small>mdi-cart-outline</v-icon>
							<span>{{ "store.view_cart" | trans }}</span>
						</v-btn>

						<!-- Reassurance Badges -->
						<div class="reassurance-box pt-3">
							<div class="d-flex align-center mb-2">
								<v-icon small color="primary" class="mr-2">mdi-shield-check-outline</v-icon>
								<span class="caption text-secondary-custom">Compra 100% segura</span>
							</div>
							<div class="d-flex align-center">
								<v-icon small color="primary" class="mr-2">mdi-truck-fast-outline</v-icon>
								<span class="caption text-secondary-custom">Envíos directos y garantizados</span>
							</div>
						</div>
					</v-card>
				</v-col>
			</v-row>

			<!-- Similar Products Section -->
			<div v-if="starred.length" class="mt-12">
				<div class="d-flex align-center mb-6">
					<v-icon color="primary" class="mr-2">mdi-rhombus-split</v-icon>
					<h3 class="text-h6 font-weight-bold text-slate-800 mb-0">
						{{ "products" | trans }} {{ "similares" | trans }}
					</h3>
				</div>
				<v-row>
					<v-col cols="12" sm="6" md="4" lg="3" v-for="p in starred" :key="p.id">
						<product-card @click:image="openDialog(p)" :product="p"></product-card>
					</v-col>
				</v-row>
			</div>
		</v-container>

		<!-- Loading State -->
		<v-container v-else class="text-center py-16">
			<v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
			<p class="mt-4 text-secondary-custom">{{ "store.loading" | trans }}</p>
		</v-container>

		<!-- Dialog Modal for Similar Products Quick View -->
		<v-dialog v-model="dialog" max-width="1000px" scrollable content-class="quick-view-dialog">
			<v-card v-if="product" class="modern-dialog-card rounded-xl">
				<v-btn
					icon
					class="dialog-close-btn"
					@click="
						dialog = false;
						product = null;
					"
				>
					<v-icon>mdi-close</v-icon>
				</v-btn>
				<product-description :product="product"></product-description>
			</v-card>
		</v-dialog>
	</div>
</template>
<script>
import ProductCard from "@/components/ProductCard";
import ProductDescription from "@/components/ProductDescription";
import StockSelector from "@/components/StockSelector";
import ProductZoom from "@/components/ProductZoom";
export default {
	components: { ProductCard, ProductDescription, StockSelector, ProductZoom },
	async mounted() {
		this.settings = await this.api.loadSettings();
	},
	data() {
		return {
			producto: null,
			starred: [],
			quantity: 1,
			dialog: false,
			stockSelected: null,
			product: null,
			selectedImageIndex: 0,
			settings: {
				collection: {},
			},
		};
	},
	methods: {
		selectImage(index) {
			this.selectedImageIndex = index;
			if (this.producto && this.producto.images && this.producto.images[index]) {
				this.producto.image_url = this.producto.images[index].url;
			}
		},
		nextImage() {
			if (!this.producto || !this.producto.images || !this.producto.images.length) return;
			const nextIdx = (this.selectedImageIndex + 1) % this.producto.images.length;
			this.selectImage(nextIdx);
		},
		prevImage() {
			if (!this.producto || !this.producto.images || !this.producto.images.length) return;
			const prevIdx = (this.selectedImageIndex - 1 + this.producto.images.length) % this.producto.images.length;
			this.selectImage(prevIdx);
		},
		addProduct(qty = 1) {
			this.quantity += qty;
			if (this.quantity < 0) this.quantity = 0;
		},
		addToCart() {
			this.$set(this.producto, "cantidad_pedidos", this.quantity);
			this.api.cart.addToCart(this.producto, false, this.stockSelected);
		},
		openDialog(product) {
			this.dialog = true;
			this.product = product;
			console.log(product);
		},
	},
	props: ["reference"],
	watch: {
		reference: {
			immediate: true,
			async handler() {
				const res = await this.api.load(
					`productos?where[referencia]=${encodeURIComponent(
						this.reference
					)}&include=images,categoria,categorias&limit=1&scope[onlyStore]=1`
				);
				this.producto = res ? res[0] : null;
				this.selectedImageIndex = 0;
				window.scrollTo(0, 0);
				if (this.producto) {
					this.producto.impuesto = parseFloat(this.producto.impuesto);
					if (this.producto.images && !this.producto.images.find((i) => i.id == this.producto.image_id)) {
						this.producto.images.unshift({
							id: this.producto.image_id,
							url: this.producto.image_url,
						});
					}
					if (this.producto.categorias && !this.producto.categorias.find((i) => i.id == this.producto.categoria_id)) {
						this.producto.categorias.unshift(this.producto.categoria);
					}
					var item = this.api.cart.find(this.producto.id);
					if (item) this.quantity = item.cantidad_pedidos;
					if (this.producto.categoria_id) {
						this.starred = (
							await this.api.get(
								`productos?limit=9&whereNot[id]=${this.producto.id}&inRandomOrder=1&where[categoria_id]=${this.producto.categoria_id}&scope[onlyStore]=1`
							)
						).data;
					}
				}
			},
		},
	},
};
</script>

<style scoped>
.product-page {
	background-color: #f8fafc;
	min-height: 100vh;
}

.gallery-card {
	background-color: #ffffff !important;
	border: 1px solid #eef2f6 !important;
}

.thumbnail-item {
	padding: 2px;
	border-radius: 8px;
	border: 2px solid transparent;
	cursor: pointer;
	background-color: #f8fafc;
	transition: all 0.2s ease;
}

.thumbnail-item:hover {
	border-color: #cbd5e1;
}

.thumbnail-item.active {
	border-color: #3b82f6;
	background-color: #ffffff;
}

.category-pill-lg {
	display: inline-block;
	font-size: 0.78rem;
	font-weight: 600;
	padding: 4px 10px;
	border-radius: 6px;
	background-color: #eff6ff;
	color: #2563eb;
	cursor: pointer;
}

.category-pill-lg:hover {
	background-color: #dbeafe;
}

.product-detail-title {
	font-size: 1.65rem;
	line-height: 1.25;
	letter-spacing: -0.01em;
	color: #0f172a;
}

.meta-badges {
	gap: 8px;
}

.meta-tag {
	font-size: 0.85rem;
	display: inline-flex;
	align-items: center;
}

.product-description-container {
	font-size: 0.925rem;
	line-height: 1.6;
}

.product-description-text {
	color: #334155;
}

.share-btn {
	background-color: #f8fafc !important;
	border: 1px solid #e2e8f0;
	transition: all 0.15s ease;
}

.share-btn:hover {
	background-color: #f1f5f9 !important;
	transform: translateY(-2px);
}

.purchase-card {
	background-color: #ffffff !important;
	border: 1px solid #e2e8f0 !important;
	position: sticky;
	top: 90px;
	box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05) !important;
}

.main-price-display {
	font-size: 1.75rem;
	color: #0f172a;
	letter-spacing: -0.02em;
}

.quantity-stepper {
	background-color: #f8fafc;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	padding: 2px 4px;
}

.stepper-btn {
	color: #475569;
}

.stepper-input {
	max-width: 60px;
	font-weight: 600;
}

.stepper-input input {
	text-align: center !important;
}

.add-to-cart-cta {
	font-weight: 600;
	letter-spacing: 0;
}

.reassurance-box {
	border-top: 1px solid #f1f5f9;
}

.hover-underline:hover {
	text-decoration: underline;
}

.text-slate-900 {
	color: #0f172a;
}

.text-slate-800 {
	color: #1e293b;
}

.text-slate-700 {
	color: #334155;
}

.text-secondary-custom {
	color: #64748b;
}

.modern-dialog-card {
	background-color: #ffffff !important;
	position: relative;
}

.dialog-close-btn {
	position: absolute;
	right: 12px;
	top: 12px;
	z-index: 10;
	background-color: rgba(241, 245, 249, 0.8) !important;
}
</style>
